import { ArrowPathIcon, FunnelIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import ProductCatalogSelectFilter from '../productCatalog/ProductCatalogSelectFilter';
import { useEffect, useState } from 'react';
import { useDebounce } from '../../helpers/useDebounce';

const MobileFiltersSheet = ({ onClose, children, footer }) => {
    useEffect(() => {
        const original = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = original;
        };
    }, []);

    return (
        <div className="fixed inset-0 z-50 md:hidden">
            <div className="absolute inset-0 bg-black/40" onClick={onClose} />
            <div className="absolute inset-x-0 bottom-0 max-h-[85vh] bg-white rounded-t-2xl flex flex-col">
                <div className="flex justify-center pt-3">
                    <div className="h-1.5 w-12 rounded-full bg-gray-300" />
                </div>
                <div className="relative px-4 py-3">
                    <h3 className="text-base font-semibold text-gray-900">Filters</h3>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close"
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                        <XMarkIcon className="h-5 w-5" />
                    </button>
                </div>
                <div className="flex-1 overflow-y-auto px-4 pb-4">{children}</div>
                {footer && <div className="border-t border-gray-200 p-3">{footer}</div>}
            </div>
        </div>
    );
};

export const DemandPlanningFilters = ({
    searchTerm = '',
    onSearchChange,
    categoryFilterOptions = [],
    subcategoryFilterOptions = [],
    statusFilterOptions = [],
    selectedCategoryId = null,
    selectedSubcategoryId = null,
    selectedStatusId = null,
    isCategorySettingsLoading = false,
    onCategoryIdChange,
    onSubcategoryIdChange,
    onStatusIdChange,
    onClearFilters,
    showUndoRestoreButton = false,
    onOpenUndoRestore,
    showChangeHistoryButton = false,
    onOpenChangeHistory,
    activeViewId = null,
    onSelectView,
    mobileFiltersSheetEnabled = false,
    mobileColumnOptions = [],
    mobileVisibleColumnIds = [],
    onToggleMobileColumn,
}) => {

    const [currentSearchTerm, setCurrentSearchTerm] = useState(searchTerm);
    const [mobileSheetOpen, setMobileSheetOpen] = useState(false);
    const debouncedSearchTerm = useDebounce(currentSearchTerm, 500);
    const activeFilterCount =
        (selectedCategoryId?.length ? 1 : 0) +
        (selectedSubcategoryId?.length ? 1 : 0) +
        (selectedStatusId?.length ? 1 : 0);
    const showBuyPlanningViewSwitcher =
        activeViewId === 'open-to-buy' || activeViewId === 'upcoming-buys';

    useEffect(() => {
        setCurrentSearchTerm(searchTerm);
    }, [searchTerm]);

    useEffect(() => {
        onSearchChange?.(debouncedSearchTerm);
    }, [debouncedSearchTerm]);

    return (
        <section className={`flex flex-col gap-3 ${mobileFiltersSheetEnabled ? 'py-2 md:py-3' : 'py-3'}`}>
            <div className="flex flex-col gap-3 2xl:flex-row 2xl:items-center 2xl:justify-between">
                {mobileFiltersSheetEnabled && (
                    <div className="flex items-center gap-2 md:hidden">
                        <div className="relative min-w-0 flex-1">
                            <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                            <input
                                className="px-3 py-2 w-full transition-all duration-200 rounded-md border border-gray-300 bg-white pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                                placeholder="Search SKU or product"
                                value={currentSearchTerm}
                                onChange={(event) => setCurrentSearchTerm(event.target.value)}
                            />
                        </div>
                        <button
                            type="button"
                            onClick={() => setMobileSheetOpen(true)}
                            className="relative inline-flex h-9 shrink-0 items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 text-sm font-medium text-gray-900 shadow-sm transition-colors hover:bg-gray-50"
                        >
                            <FunnelIcon className="h-4 w-4" />
                            Filters
                            {activeFilterCount > 0 && (
                                <span className="ml-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-xs font-bold text-white">
                                    {activeFilterCount}
                                </span>
                            )}
                        </button>
                    </div>
                )}
                <div className={`flex-1 gap-2 grid-cols-1 md:grid-cols-2 xl:grid-cols-[minmax(290px,1fr)_190px_280px_190px] ${mobileFiltersSheetEnabled ? 'hidden md:grid' : 'grid'}`}>
                    <div className="relative flex-1">
                        <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                        <input
                            className="px-3 py-2 w-full transition-all duration-200 rounded-md border border-gray-300 bg-white pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                            placeholder="Search by SKU or Product Name"
                            value={currentSearchTerm}
                            onChange={(event) => setCurrentSearchTerm(event.target.value)}
                        />
                    </div>
                    {/* Filters */}
                    <ProductCatalogSelectFilter
                        allLabel="All categories"
                        options={categoryFilterOptions}
                        selectedOptions={selectedCategoryId}
                        isLoading={isCategorySettingsLoading}
                        onChange={(next) => onCategoryIdChange?.(next)}
                        renderOption={(option) => (
                            <span className="text-gray-900">{option.label}</span>
                        )}
                    />
                    <ProductCatalogSelectFilter
                        allLabel="All subcategories"
                        options={subcategoryFilterOptions}
                        selectedOptions={selectedSubcategoryId}
                        isLoading={isCategorySettingsLoading}
                        onChange={(next) => onSubcategoryIdChange?.(next)}
                        renderOption={(option) => (
                            <span className="text-gray-900">{option.label}</span>
                        )}
                    />
                    <ProductCatalogSelectFilter
                        allLabel="All statuses"
                        options={statusFilterOptions}
                        selectedOptions={selectedStatusId}
                        onChange={(next) => onStatusIdChange?.(next)}
                        renderOption={(option) => (
                            <span className="text-gray-900">{option.label}</span>
                        )}
                    />
                </div>
                {showBuyPlanningViewSwitcher ? (
                    <div className="flex shrink-0 items-center gap-2 rounded-md border border-gray-300 bg-white p-1">
                        <button
                            type="button"
                            onClick={() => onSelectView?.('open-to-buy')}
                            className={`h-7 rounded px-3 text-sm select-none font-medium transition-all duration-200 ${
                                activeViewId === 'open-to-buy'
                                    ? 'bg-primary text-white hover:bg-primary/90'
                                    : 'bg-white text-gray-900 hover:bg-gray-50'
                            }`}
                        >
                            OTB
                        </button>
                        <button
                            type="button"
                            onClick={() => onSelectView?.('upcoming-buys')}
                            className={`h-7 rounded px-3 text-sm select-none font-medium transition-all duration-200 ${
                                activeViewId === 'upcoming-buys'
                                    ? 'bg-primary text-white hover:bg-primary/90'
                                    : 'bg-white text-gray-900 hover:bg-gray-50'
                            }`}
                        >
                            Upcoming Buys
                        </button>
                    </div>
                ) : null}
                <div className={`flex-wrap items-center justify-end gap-2 2xl:flex-row 2xl:flex-nowrap ${mobileFiltersSheetEnabled ? 'hidden md:flex' : 'flex'}`}>
                    <button
                        type="button"
                        onClick={() => onClearFilters?.()}
                        className="inline-flex items-center w-[135px] justify-center gap-2 px-4 py-2 rounded-md border border-gray-300 bg-white text-sm text-gray-900 shadow-sm transition-all duration-200 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                        <FunnelIcon className="h-4 w-4" />
                        <span className="whitespace-nowrap">Clear Filters</span>
                    </button>
                    {showUndoRestoreButton && (
                        <button
                            onClick={() => onOpenUndoRestore?.()}
                            className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive px-4 py-2 has-[>svg]:px-3 rounded-md bg-primary text-white hover:bg-primary/90"
                        >
                            <ArrowPathIcon className="h-4 w-4" />
                            <span className="whitespace-nowrap">Undo / Restore</span>
                        </button>
                    )}
                    {showChangeHistoryButton && (
                        <button
                            type="button"
                            onClick={() => onOpenChangeHistory?.()}
                            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-900 shadow-sm transition-all duration-200 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary"
                        >
                            Change History
                        </button>
                    )}
                </div>
            </div>
            {mobileFiltersSheetEnabled && mobileSheetOpen && (
                <MobileFiltersSheet
                    onClose={() => setMobileSheetOpen(false)}
                    footer={
                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={() => onClearFilters?.()}
                                className="inline-flex h-10 flex-1 items-center justify-center gap-1.5 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-900 shadow-sm"
                            >
                                <FunnelIcon className="h-4 w-4" />
                                Clear
                            </button>
                            <button
                                type="button"
                                onClick={() => setMobileSheetOpen(false)}
                                className="inline-flex h-10 flex-1 items-center justify-center rounded-md bg-primary text-sm font-semibold text-white shadow-sm transition-transform active:scale-[0.97]"
                            >
                                Apply
                            </button>
                        </div>
                    }
                >
                    <div className="space-y-3">
                        <div>
                            <p className="mb-1 text-[11px] font-semibold uppercase tracking-widest text-gray-400">Category</p>
                            <ProductCatalogSelectFilter
                                allLabel="All categories"
                                options={categoryFilterOptions}
                                selectedOptions={selectedCategoryId}
                                isLoading={isCategorySettingsLoading}
                                onChange={(next) => onCategoryIdChange?.(next)}
                                renderOption={(option) => (
                                    <span className="text-gray-900">{option.label}</span>
                                )}
                            />
                        </div>
                        <div>
                            <p className="mb-1 text-[11px] font-semibold uppercase tracking-widest text-gray-400">Subcategory</p>
                            <ProductCatalogSelectFilter
                                allLabel="All subcategories"
                                options={subcategoryFilterOptions}
                                selectedOptions={selectedSubcategoryId}
                                isLoading={isCategorySettingsLoading}
                                onChange={(next) => onSubcategoryIdChange?.(next)}
                                renderOption={(option) => (
                                    <span className="text-gray-900">{option.label}</span>
                                )}
                            />
                        </div>
                        <div>
                            <p className="mb-1 text-[11px] font-semibold uppercase tracking-widest text-gray-400">Status</p>
                            <ProductCatalogSelectFilter
                                allLabel="All statuses"
                                options={statusFilterOptions}
                                selectedOptions={selectedStatusId}
                                onChange={(next) => onStatusIdChange?.(next)}
                                renderOption={(option) => (
                                    <span className="text-gray-900">{option.label}</span>
                                )}
                            />
                        </div>
                        {mobileColumnOptions.length > 0 && (
                            <div>
                                <p className="mb-1 text-[11px] font-semibold uppercase tracking-widest text-gray-400">Columns</p>
                                <div className="flex flex-wrap gap-1.5">
                                    {mobileColumnOptions.map((column) => {
                                        const isVisible = mobileVisibleColumnIds.includes(column.id);
                                        return (
                                            <button
                                                key={column.id}
                                                type="button"
                                                onClick={() => onToggleMobileColumn?.(column.id)}
                                                className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                                                    isVisible
                                                        ? 'border-primary bg-primary text-white'
                                                        : 'border-gray-300 bg-white text-gray-900'
                                                }`}
                                            >
                                                {column.label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                </MobileFiltersSheet>
            )}
        </section>
    );
};